import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import logger from 'ws-scripts/modules/logger';
import { MARKER_ICONS, IDLE_FALLBACK_MS } from '../constants/mapConstants';
import { createMarkers, fitMapBounds } from '../utils/mapUtils';
import useCustomOverlay from './useCustomOverlay';
import useMapDragState from './useMapDragState';

/**
 * Build a Google Maps icon object from a MARKER_ICONS entry.
 * @param {{ url: string, size: number[], anchor: number[] }} iconConfig
 * @returns {Object} Google Maps icon descriptor
 */
const buildGoogleIcon = (iconConfig) => ({
	url: iconConfig.url,
	scaledSize: new window.google.maps.Size(...iconConfig.size),
	anchor: new window.google.maps.Point(...iconConfig.anchor)
});

/**
 * Hook to manage Google Maps markers for a set of locations.
 * Clears and recreates markers whenever the locations array changes.
 *
 * @param {google.maps.Map | null} map - The Google Maps instance (null until initialised)
 * @param {Array} locations - Array of location/tile objects
 * @param {boolean} isLoaded - Whether the Google Maps script has loaded
 * @param {number} hoveredTileIndex - Index of the currently hovered tile (for marker highlighting)
 */
const useMapMarkers = (map, locations, isLoaded, hoveredTileIndex) => {
	const markersRef = useRef([]);
	const overlayRef = useRef(null);
	const settleTimerRef = useRef(null);
	const idleListenerRef = useRef(null);
	const [markersUpdating, setMarkersUpdating] = useState(false);
	const hoveredTileIndexRef = useRef(hoveredTileIndex);
	hoveredTileIndexRef.current = hoveredTileIndex;

	// Track whether the user has intentionally moved the map (drag/zoom).
	// While true, fitBounds is suppressed so the viewport stays where the user put it.
	const userInteractedRef = useRef(false);

	// Suppresses the zoom_changed / dragend listeners during programmatic
	// fitBounds so they don't incorrectly set userInteractedRef.
	const programmaticMoveRef = useRef(false);

	// Delegate drag detection and map-click-to-close to a dedicated hook.
	const onMapClick = useCallback(() => overlayRef.current?.close(), []);
	const isDragging = useMapDragState(map, onMapClick);

	// Mark that the user has deliberately moved the viewport.
	useEffect(() => {
		if (!map || !window.google) return undefined;

		const dragListener = map.addListener('dragend', () => {
			if (!programmaticMoveRef.current) {
				userInteractedRef.current = true;
			}
		});
		const zoomListener = map.addListener('zoom_changed', () => {
			if (!programmaticMoveRef.current) {
				userInteractedRef.current = true;
			}
		});

		return () => {
			if (window.google?.maps) {
				window.google.maps.event.removeListener(dragListener);
				window.google.maps.event.removeListener(zoomListener);
			}
		};
	}, [map]);

	// Use custom overlay hook for overlay management
	const { customOverlayRef, handleMarkerClick, portalContainer, tileData } =
		useCustomOverlay({
			map,
			isDragging,
			locations
		});

	// Keep the stable ref in sync with the overlay instance so that
	// onMapClick always targets the latest overlay without re-binding.
	overlayRef.current = customOverlayRef.current;

	// Keep a ref to handleMarkerClick so the markers effect can use
	// the latest version without having it as a dependency (prevents drag snap-back)
	const handleMarkerClickRef = useRef(handleMarkerClick);
	handleMarkerClickRef.current = handleMarkerClick;

	const icons = useMemo(() => {
		if (!isLoaded || !window.google?.maps) {
			return null;
		}
		return {
			default: buildGoogleIcon(MARKER_ICONS.default),
			active: buildGoogleIcon(MARKER_ICONS.active)
		};
	}, [isLoaded]);

	useEffect(() => {
		if (!map || !window.google) {
			return undefined;
		}

		// Don't clear existing markers when locations is empty —
		// this prevents the map from resetting during loading gaps.
		if (!locations || locations.length === 0) {
			return undefined;
		}

		// Show loading overlay while markers transition
		setMarkersUpdating(true);

		// Tear down any in-flight settle signals from a previous run
		clearTimeout(settleTimerRef.current);
		if (idleListenerRef.current && window.google?.maps) {
			window.google.maps.event.removeListener(idleListenerRef.current);
			idleListenerRef.current = null;
		}

		// Clear existing markers
		markersRef.current.forEach((marker) => marker.setMap(null));
		markersRef.current = [];

		// Create new markers
		markersRef.current = createMarkers(
			map,
			locations,
			icons,
			(...args) => handleMarkerClickRef.current(...args),
			hoveredTileIndexRef
		);

		// Only auto-fit the viewport when new location data arrives,
		// NOT if the user has manually panned/zoomed the map.
		// Suppress the zoom_changed / dragend listeners during the
		// programmatic fitBounds so they don't flag a "user" interaction.
		if (!userInteractedRef.current) {
			programmaticMoveRef.current = true;
			fitMapBounds(map, markersRef.current);
		}
		// Reset so the next data change *will* auto-fit again
		userInteractedRef.current = false;

		// Use Google Maps 'idle' event as the primary signal that the
		// map has finished the fitBounds animation and tile rendering.
		// https://developers.google.com/maps/documentation/javascript/events
		const onSettled = () => {
			clearTimeout(settleTimerRef.current);
			if (idleListenerRef.current && window.google?.maps) {
				window.google.maps.event.removeListener(
					idleListenerRef.current
				);
				idleListenerRef.current = null;
			}
			programmaticMoveRef.current = false;
			setMarkersUpdating(false);
		};

		idleListenerRef.current = window.google.maps.event.addListenerOnce(
			map,
			'idle',
			onSettled
		);

		// Safety-net fallback in case 'idle' never fires (e.g. map is
		// hidden or in an edge-case state).  This generous timeout should
		// almost never be the code-path that clears the overlay.
		settleTimerRef.current = setTimeout(onSettled, IDLE_FALLBACK_MS);

		// Cleanup markers on unmount or before next run
		return () => {
			clearTimeout(settleTimerRef.current);
			if (idleListenerRef.current && window.google?.maps) {
				window.google.maps.event.removeListener(
					idleListenerRef.current
				);
				idleListenerRef.current = null;
			}
			markersRef.current.forEach((marker) => {
				try {
					marker.setMap(null);
				} catch (error) {
					logger.error('Error removing marker:', error);
				}
			});
			markersRef.current = [];
		};
	}, [map, locations, icons]);

	useEffect(() => {
		// Bring pins into view when hovering over a tile, but only if
		// some markers are currently outside the visible viewport.
		// This avoids a jarring zoom in/out when all pins are already visible.
		if (hoveredTileIndex !== -1 && map) {
			const bounds = map.getBounds();
			if (bounds) {
				const allVisible = markersRef.current.every((marker) =>
					bounds.contains(marker.getPosition())
				);
				if (!allVisible) {
					fitMapBounds(map, markersRef.current);
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- map ref is stable; only re-run when hoveredTileIndex changes
	}, [hoveredTileIndex]);

	/**
	 * Update marker icon when hoveredTileIndex changes.
	 * Only swaps the icon — does NOT move the map viewport.
	 */
	useEffect(() => {
		if (markersRef.current.length === 0 || !icons) {
			return;
		}

		// Update all marker icons based on hoveredTileIndex
		markersRef.current.forEach((marker, index) => {
			if (index === hoveredTileIndex) {
				marker.setIcon(icons.active);
			} else {
				marker.setIcon(icons.default);
			}
		});
	}, [hoveredTileIndex, icons]);

	return {
		customOverlayRef,
		portalContainer,
		tileData,
		markersUpdating
	};
};

export default useMapMarkers;
